// eslint-disable-next-line import-x/no-named-as-default -- posthog default export is the intended usage
import type posthog from "posthog-js";

import type { ExperimentId, ExperimentVariant } from "@/utils/env/experiments";
import { isValidVariant } from "@/utils/env/experiments";

import { variantAssigned } from "../events/experiment";
import { trackEvent } from "../track";
import type { LicenseInfo, UserInfo } from "../types";
import { setAmplitudeUserProperties } from "./amplitude";

let instance: typeof posthog | undefined;
// Cache of assigned variants for experiments (allows us to pre-fetch assignments, so they
// can be used immediately when needed)
const assignedVariants = new Map<ExperimentId, ExperimentVariant<ExperimentId>>();

interface PosthogConfig {
  apiKey: string;
  reverseProxy: string;
}

export async function initPosthog(config: PosthogConfig, license: LicenseInfo, user: UserInfo) {
  instance = (await import("posthog-js")).default;

  instance.init(config.apiKey, {
    api_host: config.reverseProxy || "https://eu.i.posthog.com",
    ui_host: "https://eu.posthog.com",
    defaults: "2025-11-30", // Specifies which config to use
    cross_subdomain_cookie: true, // Share session across subdomains (setup.* <-> app.*)
    session_recording: {
      recordCrossOriginIframes: true,
    },
    // Remove IP address
    before_send: (event) => {
      delete event?.properties.$ip;
      return event;
    },
  });
  identifyUser(license, user);
}

/**
 * Identify a logged-in user with posthog, but only send a limited data set.
 */
function identifyUser(license: LicenseInfo, user: UserInfo) {
  const { company, role } = user;

  instance?.identify(user.id, { company, role, type: license.type, plan: license.plan });

  // Set the company group (allows tracking by company)
  instance?.group("company", company, { id: company });
}

/**
 * Returns the variant string for a PostHog experiment/feature flag.
 * Also syncs the assignment to Amplitude (user property + variant_assigned event).
 * Returns undefined if PostHog is not initialized or the flag is not set.
 */
export function getExperimentVariant<T extends ExperimentId>(
  experimentName: T,
): ExperimentVariant<T> | undefined {
  // Check if we've already fetched this experiment assignment before -> avoid duplicating the assignment event in Amplitude
  const cached = assignedVariants.get(experimentName);
  if (cached !== undefined) return cached as ExperimentVariant<T>;

  const raw = instance?.getFeatureFlag(experimentName);
  if (!isValidVariant(experimentName, raw)) return undefined;

  assignedVariants.set(experimentName, raw);
  setAmplitudeUserProperties([[experimentName, raw]]);
  trackEvent(variantAssigned(experimentName, raw));

  return raw;
}

/**
 * Resets the posthog user (so future events aren't linked to the previous user)
 * Should be called on logout
 */
export function logoutPosthogUser() {
  instance?.reset();
  instance = undefined;
  assignedVariants.clear();
}
